import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LEG_ID_KEY, LEG_START_INDEX } from './RulerPath';
import { useSelectorWithMapId } from './BaseMapContainer';
import { selectMapRulerToolFollowAircraft, selectMapRulerToolLegData } from '../../../store';
import { isNil } from 'lodash';
import RulerLegOverlayComponent from './RulerLegOverlayComponent';

export const OVERLAY_Z_INDEX = 1000;
const OVERLAY_VERTICAL_OFFSET_PIXELS = 20;
const OVERLAY_OUT_OF_BOUNDS_OFFSET_PIXELS = 5;

export interface RulerLegOverlayContainerProps {
  polyline: google.maps.Polyline;
  map: google.maps.Map;
  isMarkerEventActive: boolean;
}

export const positionRulerOverlay = (
  point: google.maps.Point,
  width: number,
  height: number,
  map: google.maps.Map,
  overlayProjection: google.maps.MapCanvasProjection,
): { left: number; top: number } => {
  // Calculate the initial centered position above the mouse cursor
  let left = point.x - width / 2;
  let top = point.y - (height + OVERLAY_VERTICAL_OFFSET_PIXELS);

  // Get the map bounds
  const bounds = map.getBounds();

  // Adjust the position if the overlay is outside the bounds
  if (bounds) {
    const ne = overlayProjection.fromLatLngToDivPixel(bounds.getNorthEast());
    const sw = overlayProjection.fromLatLngToDivPixel(bounds.getSouthWest());

    if (left < sw.x + OVERLAY_OUT_OF_BOUNDS_OFFSET_PIXELS) {
      // Overlay is off the left side of the map
      left = sw.x + OVERLAY_OUT_OF_BOUNDS_OFFSET_PIXELS;
    } else if (left + width > ne.x - OVERLAY_OUT_OF_BOUNDS_OFFSET_PIXELS) {
      // Overlay is off the right side of the map
      left = ne.x - (width + OVERLAY_OUT_OF_BOUNDS_OFFSET_PIXELS);
    }

    if (top < ne.y) {
      // Overlay is off the top of the map
      top = point.y + height;
    }
  }

  return { left, top };
};

const RulerLegOverlayContainer: React.FC<RulerLegOverlayContainerProps> = (props: RulerLegOverlayContainerProps) => {
  const { polyline, map, isMarkerEventActive } = props;

  const containerElement = useRef<HTMLDivElement | null>(document.createElement('div'));
  const mouseOverEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseOutEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseMoveEventListener = useRef<google.maps.MapsEventListener | null>(null);

  const [overlayView] = useState(new google.maps.OverlayView());
  const [visible, setVisible] = useState(false);
  const [displayLocation, setDisplayLocation] = useState<google.maps.LatLng>(polyline.getPath().getAt(LEG_START_INDEX));

  const legData = useSelectorWithMapId(selectMapRulerToolLegData);
  const followAircraftEnabled = useSelectorWithMapId(selectMapRulerToolFollowAircraft);

  const legIndex = polyline.get(LEG_ID_KEY);

  const handleLegMouseOver = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!isNil(event?.latLng)) {
        setDisplayLocation(event.latLng);

        // Manually trigger mousemove event of the map so the cursor LatLng is updated
        google.maps.event.trigger(map, 'mousemove', event);
      }
      polyline.setOptions({ strokeWeight: 5 });
      setVisible(true);
    },
    [polyline, map],
  );

  const handleLegMouseOut = useCallback(() => {
    polyline.setOptions({ strokeWeight: 3 });
    setVisible(false);
  }, [polyline]);

  const handleLegMouseMove = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!isNil(event?.latLng)) {
        setDisplayLocation(event.latLng);

        // Manually trigger mousemove event of the map so the cursor LatLng is updated
        google.maps.event.trigger(map, 'mousemove', event);
      }
    },
    [map],
  );

  // Memoize the draw method to change when props change
  const drawCallback = useCallback((): void => {
    const overlayProjection = overlayView.getProjection();
    if (containerElement.current && overlayProjection && displayLocation) {
      // Get the pixel position of the display location (i.e. where the mouse is hovering)
      const point = overlayProjection.fromLatLngToDivPixel(displayLocation);

      // Temporarily set display to block to measure dimensions of the overlay
      containerElement.current.style.display = 'block';
      const { width, height } = containerElement.current.getBoundingClientRect();

      // Calculate the left and top positions of the overlay
      const { left, top } = positionRulerOverlay(point, width, height, map, overlayProjection);

      containerElement.current.style.left = `${left}px`;
      containerElement.current.style.top = `${top}px`;
      containerElement.current.style.zIndex = OVERLAY_Z_INDEX.toString();
      containerElement.current.style.display = visible ? 'block' : 'none';
    }
  }, [overlayView, displayLocation, visible, map]);

  const onAddCallback = useCallback((): void => {
    if (containerElement.current) {
      containerElement.current.style.position = 'absolute';
      containerElement.current.setAttribute('data-test', `ruler__leg__overlay__${polyline.get(LEG_ID_KEY)}`);

      const panes = overlayView.getPanes();
      if (panes) {
        panes.floatPane.appendChild(containerElement.current);
      }
    }
  }, [overlayView, polyline]);

  // Remove all event listeners
  const removePolylineEventListeners = useCallback(() => {
    if (mouseOverEventListener.current) {
      mouseOverEventListener.current.remove();
    }

    if (mouseOutEventListener.current) {
      mouseOutEventListener.current.remove();
    }

    if (mouseMoveEventListener.current) {
      mouseMoveEventListener.current.remove();
    }
  }, []);

  const onRemove = (): void => {
    if (containerElement?.current && containerElement.current.parentNode) {
      containerElement.current.parentNode.removeChild(containerElement.current);
    }
    containerElement.current = null;

    // Remove polyline listeners
    removePolylineEventListeners();
  };

  const overlayComponent: JSX.Element | null = useMemo(() => {
    // Return the overlay component
    return !isNil(legData[legIndex]?.legDistanceMiles) ? (
      <RulerLegOverlayComponent
        legData={legData}
        legIndex={legIndex}
        latLng={displayLocation}
        showAircraftETA={followAircraftEnabled}
      />
    ) : null;
  }, [legData, legIndex, displayLocation, followAircraftEnabled]);

  // Set up overlayView
  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.onAdd = onAddCallback;
    overlayView.onRemove = onRemove;
    overlayView.setMap(map);

    return () => {
      // Remove the overlay view from the map
      overlayView.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add event listeners for polyline
  useEffect(() => {
    if (!isMarkerEventActive) {
      // Add event listeners if all marker events are not currently triggered
      mouseOverEventListener.current = polyline.addListener('mouseover', handleLegMouseOver);
      mouseOutEventListener.current = polyline.addListener('mouseout', handleLegMouseOut);
      mouseMoveEventListener.current = polyline.addListener('mousemove', handleLegMouseMove);
    } else {
      // Manually trigger mouseout event
      handleLegMouseOut();

      // Remove event listeners if a marker event is currently triggered
      removePolylineEventListeners();
    }
  }, [
    polyline,
    handleLegMouseOver,
    handleLegMouseOut,
    handleLegMouseMove,
    removePolylineEventListeners,
    isMarkerEventActive,
  ]);

  // Redraw the overlay when the drawCallback changes
  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.draw();
  }, [drawCallback, overlayView]);

  // render is used to keep this component in the virtual dom. The actual
  // DOM will be set in onAdd() for the google.maps.OverlayView()
  return containerElement.current ? createPortal(overlayComponent, containerElement.current) : null;
};

export default RulerLegOverlayContainer;
